import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui';
import { useDeactivateTenantAdminForOrg } from '~/api/organizations';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';

export type TenantAdminRowData = {
  id: string;
  firstName: string;
  lastName: string;
};

interface DeactivateTenantAdminDialogProps {
  admin: TenantAdminRowData | null;
  onClose: () => void;
}

export function DeactivateTenantAdminDialog({
  admin,
  onClose,
}: DeactivateTenantAdminDialogProps) {
  const { t } = useTranslation('organizations');
  const { mutate: deactivate, isPending } = useDeactivateTenantAdminForOrg();

  if (!admin) return null;

  function handleConfirm() {
    deactivate(
      { userId: admin!.id },
      {
        onSuccess: () => {
          toast.success(t('organizations.toast.adminDeactivated'));
          onClose();
        },
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
      }
    );
  }

  return (
    <Modal isOpen onClose={onClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-full max-w-sm">
        <h2
          id="Modal-title"
          className="text-base font-semibold text-neut-prim mb-2"
        >
          {t('organizations.confirmDeactivate.title')}
        </h2>
        <p className="text-sm text-neut-sec mb-4">
          {t('organizations.confirmDeactivate.body', {
            name: `${admin.firstName} ${admin.lastName}`,
          })}
        </p>
        <div className="flex gap-3">
          <Button
            size="sm"
            variant="accent"
            onClick={onClose}
            disabled={isPending}
          >
            {t('organizations.confirmDeactivate.cancel')}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={handleConfirm}
            loading={isPending}
          >
            {t('organizations.confirmDeactivate.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
